export interface IAvailableReaction {
  code: string;
  emoji: string;
}

export class TenantConfiguration {
  allowedGroupsCount: number;
  communicationApp: string;
  disabledFeatures: string[];
  forcePrivacyPolicyCheck: boolean;
  profileEditPermissions: ProfileEditPermissions;
  tenant: string;
  approveStoryEnabled: boolean;
  allowedQuizGroupsCount: number;
  documentsEnabled: boolean;
  birthdayEnabled: boolean;
  badStoryDetectionEnabled: boolean;
  allowedTerms: boolean;
  backgroundImages: { id: number; mime: string; url: string; adminOnly: boolean }[];
  storyAsTextNumberOfRows: number;
  storyAsTextNumberOfCharactersPerRow: number;

  // theme
  defaultThemeColor: string;
  themeSecondaryButtonLabelColor?: string;
  themeSecondaryButtonIconColor?: string;
  themeLikeButtonIconColor?: string;
  themeAlertDialogCancelButtonColor?: string;
  themeAlertDialogConfirmButtonColor?: string;
  allowedStoryBackgroundImagesCount?: number;
  allowedAdminOnlyStoryBackgroundImagesCount?: number;
  availableReactions: IAvailableReaction[];

  constructor(data: ITenantConfiguration) {
    this.allowedGroupsCount = data.attributes.allowed_groups_count;
    this.communicationApp = data.attributes.communication_app;
    this.defaultThemeColor = data.attributes.default_theme_color;
    this.forcePrivacyPolicyCheck = data.attributes.force_privacy_policy_check;
    this.disabledFeatures = data.attributes.disabled_features;
    this.profileEditPermissions = {
      description: data.attributes.profile_edit_permissions.description,
      phoneNumber: data.attributes.profile_edit_permissions.phone_number,
      profileImage: data.attributes.profile_edit_permissions.profile_image,
    };
    this.tenant = data.attributes.tenant;
    this.approveStoryEnabled = data.attributes.feature_story_approval;
    this.allowedQuizGroupsCount = data.attributes.allowed_quiz_groups_count;
    this.documentsEnabled = data.attributes.feature_documents;
    this.birthdayEnabled = data.attributes.feature_birthday;
    this.allowedTerms = data.attributes.allowed_terms;
    this.backgroundImages = (data.attributes.background_images ?? []).map((image) => ({
      id: image.id,
      mime: image.mime,
      url: image.url,
      adminOnly: image.admin_only ?? false,
    }));
    this.storyAsTextNumberOfRows = data.attributes.story_as_text_number_of_rows;
    this.storyAsTextNumberOfCharactersPerRow =
      data.attributes.story_as_text_number_of_characters_per_row;
    this.badStoryDetectionEnabled = data.attributes?.feature_bad_story_detection;
    this.themeSecondaryButtonLabelColor = data.attributes?.theme_secondary_button_label_color;
    this.themeSecondaryButtonIconColor = data.attributes?.theme_secondary_button_icon_color;
    this.themeLikeButtonIconColor = data.attributes?.theme_like_button_icon_color;
    this.themeAlertDialogCancelButtonColor =
      data.attributes?.theme_alert_dialog_cancel_button_color;
    this.themeAlertDialogConfirmButtonColor =
      data.attributes?.theme_alert_dialog_confirm_button_color;
    this.allowedStoryBackgroundImagesCount = data.attributes?.allowed_story_background_images_count;
    this.allowedAdminOnlyStoryBackgroundImagesCount =
      data.attributes?.allowed_admin_only_story_background_images_count;
    this.availableReactions = data.attributes?.available_reactions ?? [];
  }
}

export interface ProfileEditPermissions {
  profileImage: boolean;
  phoneNumber: boolean;
  description: boolean;
}

// Interface
export interface ITenantConfiguration {
  attributes: {
    allowed_groups_count: number;
    communication_app: string;
    disabled_features: string[];
    force_privacy_policy_check: boolean;
    profile_edit_permissions: IProfileEditPermissions;
    tenant: string;
    feature_story_approval: boolean;
    allowed_quiz_groups_count: number;
    feature_documents: boolean;
    feature_birthday: boolean;
    allowed_terms: boolean;
    feature_bad_story_detection: boolean;
    background_images: { id: number; mime: string; url: string; admin_only: boolean }[];
    story_as_text_number_of_characters_per_row: number;
    story_as_text_number_of_rows: number;

    default_theme_color: string;
    theme_secondary_button_label_color: string;
    theme_secondary_button_icon_color: string;
    theme_like_button_icon_color: string;
    theme_alert_dialog_cancel_button_color: string;
    theme_alert_dialog_confirm_button_color: string;
    allowed_story_background_images_count: number;
    allowed_admin_only_story_background_images_count?: number;
    available_reactions?: IAvailableReaction[];
  };
}

export interface IProfileEditPermissions {
  profile_image: boolean;
  phone_number: boolean;
  description: boolean;
}
